import { jsx as _jsx } from "react/jsx-runtime";
import { useTrackClickedSubmitCollectionForm, useTrackSubmittedCollectionForm } from "@air/analytics";
import { ShortUrl } from "@air/api";
import { Button } from "@air/primitive-button";
import { useAirModal } from "@air/provider-modal";
import { useShortIdContext } from "@air/provider-short-id";
import { useToasts } from "@air/provider-toast";
import { resetUploadsAction, uploadsCurrentlyUploadingSelector } from "@air/redux-uploader";
import { reportErrorToBugsnag } from "@air/utils-error";
import pluralize from "pluralize";
import { memo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ACTION_BAR_SUBMIT } from "~/constants/testIDs";
import { clearBoardCollectionClipsAction, setAttemptedSubmissionAction, setBoardCollectionViewAction, setHasSubmittedAction } from "~/store/boardCollection/actions";
import { boardCollectionClipsSelector, boardCollectionFormIsValidSelector, boardCollectionFormSelector, boardCollectionSessionIdSelector } from "~/store/boardCollection/selectors";
import { useAirStore } from "~/utils/ReduxUtils";
import { BoardCollectionSubmissionModal } from "./BoardCollectionSubmissionModal";
export const BoardCollectionSubmitButton = /*#__PURE__*/ memo(()=>{
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();
    const store = useAirStore();
    const { showToast } = useToasts();
    const clips = useSelector(boardCollectionClipsSelector);
    const { shortId } = useShortIdContext();
    const uploads = useSelector(uploadsCurrentlyUploadingSelector);
    const { trackSubmittedCollectionForm } = useTrackSubmittedCollectionForm();
    const { trackClickedSubmitCollectionForm } = useTrackClickedSubmitCollectionForm();
    const numberOfUploadedClips = Object.keys(clips).filter((clipId)=>!uploads.find((upload)=>upload.clipId === clipId)).length;
    const [showBoardCollectionSubmissionModal, closeBoardCollectionSubmissionModal] = useAirModal(BoardCollectionSubmissionModal);
    const _submit = useCallback(async ()=>{
        try {
            const sessionId = boardCollectionSessionIdSelector(store.getState());
            const form = boardCollectionFormSelector(store.getState());
            const valid = boardCollectionFormIsValidSelector(store.getState());
            if (!sessionId || !valid) {
                return;
            }
            setIsSubmitting(true);
            await ShortUrl.submitFormSession({
                shortId,
                formId: form.id,
                sessionId
            });
            dispatch(setHasSubmittedAction({
                hasSubmitted: true
            }));
            dispatch(setBoardCollectionViewAction({
                view: "completed"
            }));
            dispatch(resetUploadsAction());
            dispatch(clearBoardCollectionClipsAction());
            trackSubmittedCollectionForm();
            setIsSubmitting(false);
        } catch (error) {
            setIsSubmitting(false);
            showToast("Failed to submit items", {
                color: "red"
            });
            reportErrorToBugsnag({
                error,
                context: "Failed to submit board collection"
            });
        }
    }, [
        dispatch,
        shortId,
        showToast,
        store,
        trackSubmittedCollectionForm
    ]);
    const submit = useCallback(async ()=>{
        const sessionId = boardCollectionSessionIdSelector(store.getState());
        const valid = boardCollectionFormIsValidSelector(store.getState());
        dispatch(setAttemptedSubmissionAction({
            attemptedSubmission: true
        }));
        trackClickedSubmitCollectionForm();
        if (!sessionId || !valid) {
            return;
        }
        showBoardCollectionSubmissionModal({
            onSubmit: async ()=>{
                await _submit();
                closeBoardCollectionSubmissionModal();
            },
            isLoading: isSubmitting
        });
    }, [
        _submit,
        closeBoardCollectionSubmissionModal,
        dispatch,
        isSubmitting,
        showBoardCollectionSubmissionModal,
        store,
        trackClickedSubmitCollectionForm
    ]);
    if (numberOfUploadedClips < 1) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Button, {
        appearance: "filled",
        color: "blue",
        size: "large",
        onClick: submit,
        isLoading: isSubmitting,
        disabled: numberOfUploadedClips < 1,
        "data-testid": ACTION_BAR_SUBMIT,
        children: "Submit ".concat(pluralize("item", numberOfUploadedClips, true))
    });
});
BoardCollectionSubmitButton.displayName = "BoardCollectionSubmitButton";
